import {
  toastUploadError,
  toastUploadResult,
} from 'Features/Projects/Table/uploadToasts'
import { Box } from '@mui/material'
import { ProductSetting } from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'
import { GridColumnGroupingModel } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Projects/Table/BulkUpdateModal'
import { useSettingsContext } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { toast } from 'react-hot-toast'
import { useUploadProductSettings } from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.data'
import {
  editsFromRows,
  useLinePreview,
  useSaveLineSettings,
} from '../lineRecalculation'
import {
  LinePreviewNote,
  toastLineSaved,
  toastLineSaveFailed,
} from '../LinePreviewNote'
import {
  cycleOverrideColumn,
  setByColumn,
  showProblems,
  useCycleGrid,
} from '../cycleColumns'

type ProductSettingsTableProps = {
  data: ProductSetting[]
  isLoading?: boolean
}

const OVERRIDES = ['minCycleOverride', 'maxCycleOverride']

const number = (digits: number) => (value: number | null) =>
  value === null || value === undefined ? '' : value.toFixed(digits)

// Grouped left to right as the rules run: demand, what it gives on its own,
// the planner's overrides, and the result the template generator uses.
const COLUMN_GROUPS: GridColumnGroupingModel = [
  {
    groupId: 'Demand',
    children: [
      { field: 'skuSalesRate' },
      { field: 'workCentreAllocation' },
      { field: 'allocatedSalesRate' },
      { field: 'minimumProductionOrderQuantity' },
    ],
  },
  {
    groupId: 'From the rules',
    description: 'What the MOQ and the batch give, before overrides',
    children: [
      { field: 'naturalCycle' },
      { field: 'initialCycle' },
      { field: 'minBatchCycleInput' },
    ],
  },
  {
    groupId: 'Overrides',
    description: 'Type 1, 2, 4 or 8 on a cell; Delete clears it',
    children: [{ field: 'minCycleOverride' }, { field: 'maxCycleOverride' }],
  },
  {
    groupId: 'Result',
    description: 'What the template generator uses',
    children: [
      { field: 'minCycleInput' },
      { field: 'maxCycleInput' },
      { field: 'cycleSetBy' },
      { field: 'replenishmentTypeCode' },
      { field: 'isIncluded' },
    ],
  },
]

const INITIAL_STATE = {
  pagination: { paginationModel: { pageSize: 100 } },
  columns: {
    columnVisibilityModel: {
      productFormat: false,
      unitOfMeasureCode: false,
      isOverriden: false,
    },
  },
}

/**
 * SKU production. Edits are previewed and saved through the API's cycle
 * rules (lineRecalculation.ts): the derived columns of every row on the
 * edited lines, batch-mates included, update as you edit, and any edit the
 * rules refuse is marked on its cell and blocks Save.
 */
export const ProductSettingsTable = ({
  data,
  isLoading,
}: ProductSettingsTableProps) => {
  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const preview = useLinePreview({
    kind: 'products',
    projectId,
    scenarioId,
    data,
  })
  const cycleGrid = useCycleGrid({
    kind: 'products',
    data,
    overrideFields: OVERRIDES,
    previewApiRef: preview.apiRefCallback,
    problemsAt: preview.problemsAt,
  })
  const { mutate: save, isPending: isSaving } = useSaveLineSettings(
    projectId,
    scenarioId,
    toastLineSaved,
    (error) => {
      preview.saveFailed(error)
      toastLineSaveFailed(error)
    }
  )
  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadProductSettings(toastUploadResult, toastUploadError)

  const problems = cycleGrid.problems
  const columns: ExtendedGridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Site',
        width: 96,
        type: 'string',
      },
      {
        field: 'workcentreCode',
        headerName: 'Work centre',
        width: 112,
        type: 'string',
      },
      {
        field: 'skuGroupCode',
        headerName: 'SKU group',
        width: 120,
        type: 'string',
      },
      {
        field: 'skuGroupName',
        headerName: 'SKU group name',
        width: 180,
        type: 'string',
      },
      {
        field: 'batchGroupingCode',
        headerName: 'Batch group',
        width: 112,
        type: 'string',
      },
      {
        field: 'skuSalesRate',
        headerName: 'Sales rate',
        description: 'Weekly demand for the SKU group at this site',
        width: 104,
        type: 'number',
        valueFormatter: number(2),
      },
      {
        field: 'workCentreAllocation',
        headerName: 'Allocation',
        description: 'Share of the demand made on this line (Allocations tab)',
        width: 96,
        type: 'number',
        valueFormatter: (value: number | null) =>
          value === null || value === undefined
            ? ''
            : `${Number((value * 100).toFixed(2))}%`,
      },
      {
        field: 'allocatedSalesRate',
        headerName: 'Allocated',
        description: 'Sales rate × allocation: the weekly demand on this line',
        width: 104,
        type: 'number',
        valueFormatter: number(2),
      },
      showProblems(
        {
          field: 'minimumProductionOrderQuantity',
          headerName: 'MOQ',
          description: 'Minimum production order quantity, in product units',
          width: 104,
          type: 'number',
          editable: true,
        },
        problems
      ),
      {
        field: 'naturalCycle',
        headerName: 'Natural',
        description: 'Weeks one MOQ lasts: MOQ ÷ allocated sales rate',
        width: 88,
        type: 'number',
        valueFormatter: number(2),
      },
      {
        field: 'initialCycle',
        headerName: 'Initial',
        description: 'The natural cycle rounded up to 1, 2, 4 or 8',
        width: 80,
        type: 'number',
      },
      {
        field: 'minBatchCycleInput',
        headerName: 'Batch min',
        description:
          "Its batch's min cycle: a SKU group can't run more often than its batch",
        width: 96,
        type: 'number',
      },
      cycleOverrideColumn({
        field: 'minCycleOverride',
        headerName: 'Min override',
        description:
          'Run no more often than this. Type 1, 2, 4 or 8; Delete clears it.',
        bound: 'min',
        minInput: 'minCycleInput',
        maxInput: 'maxCycleInput',
        problems,
      }),
      cycleOverrideColumn({
        field: 'maxCycleOverride',
        headerName: 'Max override',
        description:
          "Run at least this often. Can't be faster than its batch's min cycle.",
        bound: 'max',
        minInput: 'minCycleInput',
        maxInput: 'maxCycleInput',
        problems,
      }),
      {
        field: 'minCycleInput',
        headerName: 'Min cycle',
        width: 96,
        type: 'number',
      },
      {
        field: 'maxCycleInput',
        headerName: 'Max cycle',
        width: 96,
        type: 'number',
      },
      setByColumn(),
      {
        field: 'replenishmentTypeCode',
        headerName: 'Type',
        description:
          'FOC: a fixed cycle. FOQ: one MOQ lasts longer than its cycle, so it runs every 8 weeks with a fixed quantity.',
        width: 80,
        type: 'string',
      },
      {
        field: 'isIncluded',
        headerName: 'Included',
        width: 88,
        type: 'boolean',
      },
      showProblems(
        {
          field: 'runRate',
          headerName: 'Run rate',
          width: 104,
          type: 'number',
          editable: true,
          valueFormatter: number(2),
        },
        problems
      ),
      {
        field: 'isPrimaryWorkCentre',
        headerName: 'Primary line',
        width: 104,
        type: 'boolean',
      },
      {
        field: 'productFormat',
        headerName: 'Product format',
        width: 130,
        type: 'string',
      },
      {
        field: 'unitOfMeasureCode',
        headerName: 'Unit of measure',
        width: 130,
        type: 'string',
      },
      {
        field: 'isOverriden',
        headerName: 'Is overridden',
        description: 'Min cycle differs from the initial cycle',
        width: 112,
        type: 'boolean',
      },
    ]
  }, [problems])

  const updateTableData = (changedRows: Record<number, ProductSetting>) => {
    const before = new Map(data.map((row) => [row.id, row]))
    const products = editsFromRows(
      'products',
      Object.values(changedRows).map((current) => ({
        current,
        before: before.get(current.id),
      }))
    )
    if (!products.length) {
      toast('No input changes to save')
      return
    }
    preview.holdForSave()
    save({ products })
  }

  const uploadData = (file: File) => {
    uploadMutate({ file, projectId, scenarioId })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box sx={{ px: 2, pb: 2 }}>{cycleGrid.quickFilters}</Box>
      <LinePreviewNote
        summary={preview.summary}
        isPreviewing={preview.isPreviewing}
        problems={preview.problems}
      />
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading ?? true}
        updateTableData={updateTableData}
        onPendingChange={preview.onPendingChange}
        getCellClassName={preview.getCellClassName}
        apiRefCallback={cycleGrid.apiRefCallback}
        onCellKeyDown={cycleGrid.onCellKeyDown}
        onFilterModelChange={cycleGrid.onFilterModelChange}
        saveBlockedReason={preview.saveBlockedReason}
        cellSelection
        columnGroupingModel={COLUMN_GROUPS}
        initialState={INITIAL_STATE}
        uploadData={uploadData}
        showTableToolBar={true}
        isUploading={isUploading ?? false}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={['baseSourceSiteCode', 'workcentreCode', 'skuGroupCode']}
        customDownloadDataMethod={(tableData) => tableData}
      />
    </Box>
  )
}
